import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { HelpBubble } from '@/components/HelpBubble';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  CheckCircle, AlertTriangle, XCircle, RefreshCw, ArrowUpRight,
  Database, Shield, TrendingUp, Lightbulb, ChevronDown, ChevronUp, ChevronRight,
} from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import api from '@/lib/api';
import { AIPanel } from '@/components/AIPanel';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  complete: { icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', badge: 'bg-emerald-100 text-emerald-800' },
  partial: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', badge: 'bg-amber-100 text-amber-800' },
  missing: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50 border-red-200', badge: 'bg-red-100 text-red-800' },
};

function ScoreRing({ score }) {
  const radius = 70;
  const stroke = 10;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (score / 100) * circ;
  const color = score >= 75 ? '#22c55e' : score >= 50 ? '#f59e0b' : '#ef4444';

  return (
    <div className="relative flex items-center justify-center" data-testid="quality-score-ring">
      <svg width="170" height="170" viewBox="0 0 170 170">
        <circle cx="85" cy="85" r={radius} fill="none" stroke="hsl(var(--border))" strokeWidth={stroke} />
        <circle cx="85" cy="85" r={radius} fill="none" stroke={color} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
          transform="rotate(-90 85 85)" style={{ transition: 'stroke-dashoffset 1s ease' }} />
      </svg>
      <div className="absolute text-center">
        <p className="text-4xl font-bold font-mono" style={{ color }} data-testid="quality-score-value">{score}</p>
        <p className="text-xs text-muted-foreground">out of 100</p>
      </div>
    </div>
  );
}

function FieldBar({ field, fill_pct }) {
  const displayName = field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const color = fill_pct >= 80 ? 'bg-emerald-500' : fill_pct >= 20 ? 'bg-amber-500' : 'bg-red-400';

  return (
    <div className="flex items-center gap-3 py-1">
      <span className="text-xs font-mono text-muted-foreground w-40 truncate" title={field}>{displayName}</span>
      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.max(fill_pct, 2)}%`, transition: 'width 0.6s ease' }} />
      </div>
      <span className={`text-xs font-mono w-12 text-right ${fill_pct >= 80 ? 'text-emerald-600' : fill_pct >= 20 ? 'text-amber-600' : 'text-red-500'}`}>
        {fill_pct}%
      </span>
    </div>
  );
}

function SectionCard({ section }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = STATUS_CONFIG[section.status];
  const Icon = cfg.icon;

  return (
    <div className={`rounded-lg border ${cfg.bg} overflow-hidden`} data-testid={`section-${section.name.toLowerCase().replace(/[^a-z]/g, '-')}`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center gap-3 p-3 hover:bg-black/5 transition-colors">
        <Icon className={`w-5 h-5 shrink-0 ${cfg.color}`} />
        <div className="flex-1 text-left min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{section.name}</span>
            <Badge className={`text-[10px] ${cfg.badge}`}>{section.completeness_pct}%</Badge>
          </div>
          <p className="text-[10px] text-muted-foreground truncate">{section.unlocks}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="w-20 h-1.5 bg-black/10 rounded-full overflow-hidden">
            <div className="h-full rounded-full bg-current" style={{ width: `${section.completeness_pct}%`, color: cfg.color === 'text-emerald-600' ? '#22c55e' : cfg.color === 'text-amber-600' ? '#f59e0b' : '#ef4444' }} />
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-3 pt-1 border-t border-black/10">
          {section.fields.map((f, i) => <FieldBar key={i} {...f} />)}
        </div>
      )}
    </div>
  );
}

export default function DataQuality() {
  const [recsOpen, setRecsOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/analytics/data-quality');
      setData(res.data);
    } catch { toast.error('Failed to load data quality metrics'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading && !data) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }

  if (!data?.has_data) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4" data-testid="no-data">
        <Database className="w-12 h-12 text-muted-foreground" />
        <p className="text-muted-foreground">No data uploaded yet. Upload your spend data to see quality metrics.</p>
      </div>
    );
  }

  const sections = data.sections || [];
  const unlocked = data.unlocked || [];
  const blocked = data.blocked || [];
  const recs = data.recommendations || [];
  const score = data.overall_score || 0;

  return (
    <div className="space-y-4" data-testid="data-quality-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Data Quality</h1>
          <p className="text-muted-foreground mt-1">
            Completeness score across {data.total_records?.toLocaleString()} records ({data.sample_size?.toLocaleString()} sampled)
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="refresh-quality">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Score + Summary Row */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
        {/* Score Ring */}
        <Card className="lg:row-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Database className="w-4 h-4 text-primary" /> Overall Score
              <HelpBubble text="SpendLens Data Score (PDS) — composite of 11 field groups weighted by analytical value. 70%+ unlocks full AI capability; under 40% blocks AI entirely." />
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4">
            <ScoreRing score={score} />
            <div className="text-center">
              <p className="text-sm font-medium">
                {score >= 75 ? 'Excellent' : score >= 50 ? 'Good — room to improve' : 'Needs attention'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {unlocked.length} feature groups active, {blocked.length} blocked
              </p>
            </div>
            <div className="flex gap-4 text-[10px]">
              <span className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> 80%+</span>
              <span className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-full bg-amber-500" /> 20-79%</span>
              <span className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-full bg-red-400" /> &lt;20%</span>
            </div>
          </CardContent>
        </Card>

        {/* Unlocked Analytics */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-600" /> Active Analytics
              <HelpBubble text="Field groups with enough data coverage to power their dependent features (dashboards, filters, and AI insights)." />
            </CardTitle>
            <CardDescription>Features enabled by your current data</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {unlocked.map((u, i) => (
                <div key={i} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-xs" data-testid="unlocked-item">
                  <CheckCircle className="w-3 h-3 text-emerald-600 shrink-0" />
                  <span className="font-medium text-emerald-800">{u.section}</span>
                  <span className="text-emerald-600/70">({u.completeness}%)</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Blocked Analytics */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <XCircle className="w-4 h-4 text-red-500" /> Blocked Analytics
              <HelpBubble text="Features that cannot run until the underlying field group reaches minimum coverage. Enrich the listed columns in your next CSV upload to unlock." />
            </CardTitle>
            <CardDescription>Features unavailable due to missing data</CardDescription>
          </CardHeader>
          <CardContent>
            {blocked.length === 0 ? (
              <p className="text-sm text-emerald-600 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> All analytics features are active!
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {blocked.map((b, i) => (
                  <div key={i} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-red-50 border border-red-200 text-xs" data-testid="blocked-item">
                    <XCircle className="w-3 h-3 text-red-500 shrink-0" />
                    <span className="font-medium text-red-800">{b.section}</span>
                    <span className="text-red-500/70">{b.analytics}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recommendations */}
      {recs.length > 0 && (
        <Collapsible open={recsOpen} onOpenChange={setRecsOpen} data-testid="recommendations-card">
          <Card>
            <CardHeader className="pb-2">
              <CollapsibleTrigger asChild>
                <button className="flex items-center gap-2 w-full text-left" data-testid="recommendations-toggle">
                  <ChevronRight className={`w-4 h-4 transition-transform ${recsOpen ? 'rotate-90' : ''}`} />
                  <Lightbulb className="w-4 h-4 text-amber-500" />
                  <CardTitle className="text-base">Recommendations</CardTitle>
                  {!recsOpen && <Badge variant="secondary" className="text-xs ml-1">{recs.length}</Badge>}
                </button>
              </CollapsibleTrigger>
              {recsOpen && <CardDescription className="ml-8">Add these fields to unlock the most analytics value</CardDescription>}
            </CardHeader>
            <CollapsibleContent>
              <CardContent className="space-y-3">
                {recs.map((r, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg border bg-muted/30" data-testid="recommendation-item">
                    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-amber-100 text-amber-700 text-xs font-bold shrink-0">{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">{r.section}</p>
                        <Badge variant="outline" className="text-[10px]">Impact: {r.impact}/100</Badge>
                        <Badge variant="outline" className="text-[10px] text-amber-700">Current: {r.current_pct}%</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Would unlock: {r.would_unlock}</p>
                      {r.missing_fields.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {r.missing_fields.map((f, j) => (
                            <span key={j} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-200">
                              {f.replace(/_/g, ' ')}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-muted-foreground shrink-0 mt-1" />
                  </div>
                ))}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {/* Field Completeness by Section */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="w-4 h-4 text-primary" /> Field Completeness by Section
          </CardTitle>
          <CardDescription>Click each section to see individual field coverage</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {sections.map((sec, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <SectionCard section={sec} />
            </motion.div>
          ))}
        </CardContent>
      </Card>
      <AIPanel page="data-quality" filters={{}} />
    </div>
  );
}
